import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import UserAvatar from '../../../../UserAvatar'; // Import the UserAvatar component
import { fixImageUrl } from '../../../../../services/imageService'; // Import this if not already available in scope

/**
 * Attendees tab content component with edit mode
 * 
 * @param {object} event - Event data
 * @param {object} user - Current user data
 * @param {boolean} canEdit - Whether the user can edit attendees
 * @param {function} onUpdateAttendance - Function to update attendance status
 * @param {function} onRemoveAttendee - Function to remove an attendee
 * @param {function} onShowAttendeesModal - Function to show the attendees modal
 * @param {boolean} isOrganizer - Whether the current user is the event organizer
 * @returns {JSX.Element} Attendees view component
 */
const AttendeesView = ({
  event,
  user,
  canEdit,
  onUpdateAttendance,
  onRemoveAttendee,
  onShowAttendeesModal,
  isOrganizer // Accept this directly from parent
}) => {
  const { t } = useLanguage();
  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  // Implement a local state cache to prevent flickering
  const [cachedStatus, setCachedStatus] = useState(null);
  
  // Get attendees from event - with null checks
  const attendees = event?.attendees ? [...event.attendees] : [];
  const currentUserId = user?.id;
  
  // Find current user in attendees (if any)
  const currentUserAttendee = attendees.find(att => String(att.user_id) === String(currentUserId));
  // Filter out current user from the list (we'll add them at the top)
  const otherAttendees = attendees.filter(att => String(att.user_id) !== String(currentUserId));
  
  // Update cached status when props change - moved before conditional rendering
  useEffect(() => {
    if (currentUserAttendee) {
      // If we get a new status from props, update our cache
      setCachedStatus(currentUserAttendee.status);
    }
  }, [currentUserAttendee]);
  
  // Helper function to get display name for an attendee
  const getAttendeeDisplayName = (attendee) => {
    return attendee.display_name || attendee.email?.split('@')[0] || t('events.attendees.defaultName');
  };

  // Helper function to determine status
  const isConfirmed = (status) => {
    return status === 'confirmed' || status === 'going' || status === 'yes';
  };
  
  const isDeclined = (status) => {
    return status === 'declined' || status === 'notgoing' || status === 'no';
  };
  
  // Helper function to render status badge for other attendees
  const renderStatusBadge = (status) => {
    if (isConfirmed(status)) {
      return <span className="badge bg-success">{t('events.attendees.confirmed')}</span>;
    } else if (isDeclined(status)) {
      return <span className="badge bg-danger">{t('events.attendees.declined')}</span>;
    } else {
      return <span className="badge bg-secondary">{t('events.attendees.pending')}</span>;
    }
  };
  
  // Handle toggle click to update attendance status
  const handleToggleClick = () => {
    // Use our cached status (which is more responsive to local changes)
    // or fall back to the status from props
    const status = cachedStatus || currentUserAttendee?.status || 'pending';
    const isUserConfirmed = isConfirmed(status);
    
    // If status is confirmed, switch to declined
    // If status is declined or pending, switch to confirmed
    const newStatus = isUserConfirmed ? 'declined' : 'confirmed';
    
    // Update our local cache immediately for responsive UI
    setCachedStatus(newStatus);
    
    
    // Pass the existing attendee flag to the parent
    const isExistingAttendee = !!currentUserAttendee;
    onUpdateAttendance(newStatus, isExistingAttendee);
  };
  
  // Handle removing an attendee
  const handleRemoveAttendee = (userId) => {
    if (window.confirm(t('events.attendees.removeConfirm'))) {
      onRemoveAttendee(userId);
    }
  };
  
  // Get current user status for toggle display - use cached status preferentially
  const status = cachedStatus || currentUserAttendee?.status || 'pending';
  const isUserConfirmed = isConfirmed(status);
  const isUserDeclined = isDeclined(status);
  
  // Custom button style to match the tabs
  const roundedButtonStyle = {
    borderRadius: '30px',
    fontSize: '0.9rem',
    paddingLeft: '15px',
    paddingRight: '15px',
    paddingTop: '6px',
    paddingBottom: '6px',
    border: editMode ? 'none' : '1px solid #3b82f6',
    backgroundColor: editMode ? '#6c757d' : 'transparent',
    color: editMode ? 'white' : '#3b82f6',
    transition: 'all 0.3s ease',
    boxShadow: 'none',
    outline: 'none',
    fontWeight: '500'
  };
  
  /* Attendees header with edit toggle */
  const renderAttendeesHeader = () => {
    return (
      <div className="attendees-header d-flex justify-content-between align-items-center mb-3">
        <div className="attendees-title">{t('events.attendees.title')}</div>
        {/* Show edit button ONLY if user is organizer */}
        {isOrganizer && (
          <button
            className="edit-mode-toggle"
            onClick={() => setEditMode(!editMode)}
            style={roundedButtonStyle}
          >
            {editMode ? t('events.attendees.finishEditing') : t('events.attendees.editAttendance')}
          </button>
        )}
      </div>
    );
  };
  
  // Early return with empty message if there are no attendees and event data
  if (!event || !event.attendees) {
    return (
      <div className="empty-content">
        <p className="text-muted">{t('events.attendees.empty')}</p>
      </div>
    );
  }
  
  return (
    <div className={`event-attendees-list ${editMode ? 'edit-mode-active' : ''}`}>
      {/* Attendees header with edit button */}
      {renderAttendeesHeader()}
      
      {/* Current user section at top with inline toggle */}
      {currentUserAttendee && (
        <div className="current-user-section mb-3">
          <div className="list-group-item current-user">
            <div className="d-flex w-100 justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                {/* Use current user's profile image from user prop */}
                <div className="me-2">
                  <UserAvatar
                    profileImage={user.profileImage}
                    displayName={user.displayName || user.display_name}
                    email={user.email}
                    size={46}
                    avatarOnly={true}
                  />
                </div>
                <div>
                  <h6 className="mb-1 attendee-name">
                    {getAttendeeDisplayName(currentUserAttendee)}
                  </h6>
                  <div>
                    <small className="badge bg-info">{t('events.attendees.you')}</small>
                    {isOrganizer && <small className="badge bg-warning ms-1">{t('events.attendees.organizer')}</small>}
                  </div>
                </div>
              </div>
              
              {/* Inline Toggle Switch */}
              <div className="d-flex align-items-center">
                {/* Label for "Ne pridem" */}
                <span className="me-1" style={{
                    fontSize: '0.75rem',
                    color: isUserDeclined ? '#dc3545' : '#6c757d',
                    fontWeight: isUserDeclined ? 'bold' : 'normal'
                }}>
                    {t('events.attendees.no')}
                </span>
                
                {/* Toggle Switch */}
                <div 
                    className="attendance-toggle position-relative" 
                    style={{
                        width: '36px',
                        height: '20px',
                        borderRadius: '100px',
                        backgroundColor: isUserConfirmed ? '#3b82f6' : (isUserDeclined ? '#dc3545' : '#adb5bd'),
                        cursor: 'pointer',
                        transition: 'background-color 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 3px',
                        margin: '0 4px'
                    }}
                    onClick={handleToggleClick}
                >
                    <div 
                        className="toggle-thumb" 
                        style={{
                            position: 'absolute',
                            left: isUserConfirmed ? 'calc(100% - 16px)' : '3px',
                            width: '14px',
                            height: '14px',
                            borderRadius: '50%',
                            backgroundColor: '#fff',
                            transition: 'left 0.3s ease',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                        }}
                    ></div>
                </div>


                {/* Label for "Pridem" */}
                <span className="ms-1" style={{
                    fontSize: '0.75rem',
                    color: isUserConfirmed ? '#3b82f6' : '#6c757d',
                    fontWeight: isUserConfirmed ? 'bold' : 'normal'
                }}>
                    {t('events.attendees.yes')}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* For non-attendees, show a simple add attendance button */}
      {!currentUserAttendee && (
        <div className="add-attendance-section mb-3">
          <div className="list-group-item">
            <div className="d-flex flex-column align-items-center">
              <div className="mb-2">{t('events.attendees.confirmAttendance')}</div>
              <div className="d-flex gap-2">
                <button
                  className="btn"
                  onClick={() => onUpdateAttendance('confirmed', false)}
                  style={{
                    ...roundedButtonStyle,
                    backgroundColor: '#198754',
                    color: 'white',
                    border: 'none'
                  }}
                >
                  {t('events.attendees.attending')}
                </button>
                <button
                  className="btn"
                  onClick={() => onUpdateAttendance('declined', false)}
                  style={{
                    ...roundedButtonStyle,
                    backgroundColor: 'transparent',
                    color: '#dc3545',
                    border: '1px solid #dc3545'
                  }}
                >
                  {t('events.attendees.notAttending')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Left-aligned divider text with improved visibility */}
      {otherAttendees.length > 0 && (
        <div
          style={{
            marginTop: '20px',
            marginBottom: '16px',
            paddingLeft: '8px'
          }}
        >
          <span
            style={{
              fontSize: '0.95rem',
              fontWeight: '500',
              color: '#adb5bd',
              textAlign: 'left'
            }}
          >
            {t('events.attendees.otherAttendees')}
          </span>
        </div>
      )}
      
      {/* Other attendees */}
      {otherAttendees.length > 0 ? (
        <div className="other-attendees">
          {otherAttendees.map((attendee, index) => {
            // Check if this attendee is the organizer
            const isAttendeeOrganizer = String(attendee.user_id) === String(event.organizer_id) || 
                                        String(attendee.user_id) === String(event.owner_id);
            
            return (
              <div key={`${attendee.user_id}-${index}`} className="list-group-item">
                <div className="d-flex w-100 justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    {/* Use UserAvatar with profile_image from backend */}
                    <div className="me-2">
                      <UserAvatar
                        profileImage={attendee.profile_image}
                        displayName={attendee.display_name}
                        email={attendee.email}
                        size={46}
                        avatarOnly={true}
                      />
                    </div>
                    <div>
                      <h6 className="mb-1 attendee-name">{getAttendeeDisplayName(attendee)}</h6>
                      <div>
                        {renderStatusBadge(attendee.status)}
                        {isAttendeeOrganizer && (
                          <small className="badge bg-warning ms-1">{t('events.attendees.organizer')}</small>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Show delete button in edit mode only if user is organizer */}
                  {editMode && isOrganizer && (
                    <button
                      className="btn btn-sm"
                      onClick={() => handleRemoveAttendee(attendee.user_id)}
                      style={{
                        ...roundedButtonStyle,
                        backgroundColor: 'transparent',
                        color: '#dc3545',
                        border: '1px solid #dc3545',
                        padding: '3px 8px'
                      }}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : !currentUserAttendee && (
        <div className="empty-content">
          <p className="text-muted">{t('events.attendees.empty')}</p>
        </div>
      )}
      
      {/* "Add attendees" button when in edit mode and user is organizer */}
      {editMode && isOrganizer && (
        <div className="attendees-edit-actions d-flex justify-content-center pb-4 mt-4">
          <button
            className="btn"
            onClick={onShowAttendeesModal}
            style={{
              ...roundedButtonStyle,
              backgroundColor: '#0d6efd',
              color: 'white',
              border: 'none',
              paddingLeft: '15px',
              paddingRight: '15px'
            }}
          >
            <FontAwesomeIcon icon={faUserPlus} className="me-2" />
            {t('events.attendees.addAttendees')}
          </button>
        </div>
      )}
    </div>
  );
};

export default AttendeesView;